import { CSSProperties } from "react";
import { DEFAULT_BRAND_THEME, DEFAULT_QUEST_THEME } from "../../tutorial/components/tutorial.types";
import { extractGradientColors, hexToGlassColor } from "../../../../general";

export interface StreakQuestThemeData {
  accentColor?: string;
  theme?: string;
  borderRadius?: string;
  buttonColor?: string;
  images?: string[];
}

export interface StreakBrandTheme {
  accentColor?: string;
  background?: string;
  borderRadius?: string;
  primaryButton?: string;
  secondaryButton?: string;
  contentColor?: string;
  fontFamily?: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  titleColor?: string;
}

interface StreakThemeConfig {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
}

interface StreakStyleConfig {
  Form?: CSSProperties;
  Description?: CSSProperties;
  Heading?: CSSProperties;
  Count?: CSSProperties;
  Icon?: {
    ActiveColor?: string;
    InactiveColor?: string;
    ActiveBackGround?: string;
    InactiveBackGround?: string;
  };
  Footer?: CSSProperties;
  TimeStamp?: {
    ProgressBarBg?: string;
    ProgressBarFill?: string;
    styles?: CSSProperties;
  }
}

const QUEST_THEME: StreakQuestThemeData = DEFAULT_QUEST_THEME;
const BRAND_THEME: StreakBrandTheme = DEFAULT_BRAND_THEME;

export type PartialStreakQuestThemeData = Partial<StreakQuestThemeData>;
export type PartialStreakBrandTheme = Partial<StreakBrandTheme>;

class StreakThemeManager {
  private themeConfig: StreakThemeConfig;
  private styleConfig: StreakStyleConfig;
  private questThemeData: StreakQuestThemeData;
  private brandTheme: StreakBrandTheme;

  constructor(
    themeConfig: StreakThemeConfig = {},
    styleConfig: StreakStyleConfig = {},
    questThemeData: PartialStreakQuestThemeData = QUEST_THEME,
    brandTheme: PartialStreakBrandTheme = BRAND_THEME
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = { ...QUEST_THEME, ...questThemeData };
    this.brandTheme = { ...BRAND_THEME, ...brandTheme };
  }

  private getStyleValueAsString(path: string, defaultValue: string = "", config: any = this.styleConfig): string {
    const keys = path.split(".");
    let value: any = config;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return typeof value === "string" ? value : defaultValue;
  }

  private getStyleValueAsObject<T>(
    path: string,
    defaultValue: T,
    config: any = this.styleConfig
  ): T {
    const keys = path.split(".");
    let value: any = config;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return (typeof value === "object" ? { ...defaultValue, ...value } : defaultValue) as T;
  }

  private mergeStyles(
    baseStyles: CSSProperties,
    overrideStyles?: CSSProperties
  ): CSSProperties {
    return {
      ...baseStyles,
      ...(overrideStyles || {}),
    };
  }

  private getBackgroundColor(): string {
    return (
      this.getStyleValueAsString("Form.backgroundColor") ||
      this.brandTheme?.background ||
      this.themeConfig?.backgroundColor
    );
  }

  private getFontFamily(): string {
    return (
      this.brandTheme?.fontFamily ||
      this.themeConfig?.fontFamily ||
      "'Figtree', sans-serif"
    );
  }

  private getBorderRadius(): string {
    return (
      this.getStyleValueAsString("Form.borderRadius") ||
      this.questThemeData?.borderRadius ||
      this.brandTheme?.borderRadius ||
      "8px"
    );
  }

  getContainerStyles(): CSSProperties {
    const formStyles = this.getStyleValueAsObject<CSSProperties>("Form", {});
    return this.mergeStyles(
      {
        background: this.getBackgroundColor(),
        height: this.getStyleValueAsString("Form.height", "auto"),
        borderRadius: this.getBorderRadius(),
        fontFamily: this.getFontFamily(),
      },
      formStyles
    );
  }

  getStreakCountStyles(): CSSProperties {
    const countStyles = this.getStyleValueAsObject<CSSProperties>("Count", {});
    return this.mergeStyles(
      {
        color:
          (this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.includes("gradient") ? extractGradientColors(this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#9035ff",
      },
      countStyles
    );
  }

  getStreakHeadingStyles(): CSSProperties {
    const headingStyles = this.getStyleValueAsObject<CSSProperties>("Heading", {});
    return this.mergeStyles(
      {
        color:
          this.getStyleValueAsString("Heading.color") ||
          extractGradientColors(this.brandTheme?.titleColor ||
            this.brandTheme?.primaryColor) ||
          "#000000",
      },
      headingStyles
    );
  }

  getStreakDescriptionStyles(): CSSProperties {
    const descriptionStyles = this.getStyleValueAsObject<CSSProperties>("Description", {});
    return this.mergeStyles(
      {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.contentColor ||
          this.brandTheme?.secondaryColor ||
          "#666666",
      },
      descriptionStyles
    );
  }

  getIconStyles(isActive: boolean) {
    const iconStyles = this.getStyleValueAsObject<{
      ActiveColor?: string;
      InactiveColor?: string;
      ActiveBackGround?: string;
      InactiveBackGround?: string;
    }>("Icon", {});

    return {
      background: isActive ? iconStyles.ActiveBackGround || hexToGlassColor(this.brandTheme?.primaryColor || this.brandTheme?.accentColor, 0.1) || "#f4ebff" : iconStyles.InactiveBackGround || "#fbfbfb",
      iconColor: isActive
        ? extractGradientColors(iconStyles.ActiveColor ||
          this.brandTheme?.primaryColor ||
          this.brandTheme?.accentColor ||
          this.questThemeData?.accentColor ||
          "#9035ff")
        : iconStyles.InactiveColor ||
        "#b9b9b9",
    };
  }

  getFooterStyles(): CSSProperties {
    const footerStyles = this.getStyleValueAsObject<CSSProperties>("Footer", {});
    return this.mergeStyles(
      {
        background: this.getBackgroundColor(),
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        color: this.brandTheme?.contentColor || this.brandTheme?.secondaryColor || "#666666",
      },
      footerStyles
    );
  }

  // Common style getters
  getPrimaryColor(): string {
    return (
      this.getStyleValueAsString("Heading.color") ||
      this.brandTheme?.primaryColor ||
      this.themeConfig?.primaryColor ||
      "#000000"
    );
  }

  getSecondaryColor(): string {
    return (
      this.getStyleValueAsString("Description.color") ||
      this.brandTheme?.secondaryColor ||
      this.themeConfig?.secondaryColor ||
      "#666666"
    );
  }

  getAccentColor(): string {
    return (
      this.brandTheme?.accentColor ||
      this.questThemeData?.accentColor ||
      "#9035ff"
    );
  }

  // Update methods
  updateThemeConfig(newConfig: Partial<StreakThemeConfig>): void {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<StreakStyleConfig>): void {
    this.styleConfig = {
      ...this.styleConfig,
      ...newConfig,
      Footer: this.mergeStyles(
        this.getStyleValueAsObject<CSSProperties>("Footer", {}),
        newConfig.Footer || {}
      ),
    };
  }

  updateQuestTheme(newTheme: PartialStreakQuestThemeData): void {
    this.questThemeData = {
      ...this.questThemeData,
      ...newTheme,
    };
  }

  updateBrandTheme(newTheme: PartialStreakBrandTheme): void {
    this.brandTheme = {
      ...this.brandTheme,
      ...newTheme,
    };
  }

  getTimestampStyles(): CSSProperties {
    return this.styleConfig?.TimeStamp?.styles
  }

  getProgressBarBg(): string {
    return this.styleConfig?.TimeStamp?.ProgressBarBg || hexToGlassColor(this.brandTheme?.contentColor, 0.5)
  }

  getProgressBarFill(): string {
    return this.styleConfig?.TimeStamp?.ProgressBarFill || this.brandTheme?.primaryColor || this?.questThemeData?.accentColor
  }

  getTickIconColor(): string {
    return this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#9035ff"
  }
}

export {
  QUEST_THEME,
  BRAND_THEME,
  StreakThemeConfig,
  StreakStyleConfig
};

export default StreakThemeManager;